/**
 * @fileoverview Test Automation Leverage Page
 * @description Main page component — fetches Azure DevOps test cases with
 *              "Auto Generated Tests" field and displays KPI summary,
 *              project breakdown, and detailed data table.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { RefreshCw, Activity } from 'lucide-react';
import { testAutomationLeverageApi } from '../services/testAutomationLeverageApi';
import { SummaryCards, ProjectBreakdown, TestCasesTable, SavingsDashboard } from '../components';
import './TestAutomationLeverage.css';

const DAYS_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

export const TestAutomationLeverage = () => {
  // ── State ─────────────────────────────────────────────────────────────────
  const [testCases, setTestCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthlyMetrics, setMonthlyMetrics] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [healthy, setHealthy] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // ── Health check ──────────────────────────────────────────────────────────
  const checkHealth = useCallback(async () => {
    try {
      await testAutomationLeverageApi.health();
      setHealthy(true);
    } catch {
      setHealthy(false);
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // ── Fetch data ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tcResponse, summaryResponse, monthlyResponse] = await Promise.all([
        testAutomationLeverageApi.fetchTestCases(days),
        testAutomationLeverageApi.fetchSummary(days),
        testAutomationLeverageApi.fetchMonthlyMetrics({ days }),
      ]);

      setTestCases(tcResponse?.data?.testCases || []);
      setSummary(summaryResponse?.data || null);
      setMonthlyMetrics(monthlyResponse?.data || null);
      setLastFetched(new Date());
    } catch (err) {
      console.error('Failed to fetch test automation data:', err);
      setError(err.message || 'Failed to fetch data from Azure DevOps');
    } finally {
      setLoading(false);
    }
  }, [days]);

  // Auto-fetch on mount
  useEffect(() => {
    if (healthy) {
      fetchData();
    }
  }, [healthy]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box className="tal-page" sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Activity size={28} color="#01426A" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#01426A' }}>
              AI Leveraged Test Generation
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              AI-generated test cases tracked from Azure DevOps
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {healthy === false && (
            <Chip label="Service Offline" color="error" size="small" />
          )}
          {lastFetched && (
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Last updated: {lastFetched.toLocaleTimeString()}
            </Typography>
          )}
          <TextField
            select
            size="small"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 120 }}
            label="Time Range"
          >
            {DAYS_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                Last {d} days
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            size="small"
            startIcon={
              loading ? (
                <Spinner size={14} color="inherit" />
              ) : (
                <RefreshCw size={14} />
              )
            }
            onClick={fetchData}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#01426A',
              '&:hover': { backgroundColor: '#002F5F' },
            }}
          >
            {loading ? 'Fetching...' : 'Fetch'}
          </Button>
        </Stack>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Savings Dashboard — hero KPIs + monthly chart */}
      <SavingsDashboard metrics={monthlyMetrics} />

      {/* Summary KPI cards */}
      <SummaryCards summary={summary} testCases={testCases} />

      {/* Project breakdown */}
      {summary?.byProject?.length > 0 && (
        <ProjectBreakdown projects={summary.byProject} />
      )}

      {/* Data table */}
      <TestCasesTable testCases={testCases} loading={loading} />
    </Box>
  );
};

export default TestAutomationLeverage;
